// @ts-strict-ignore
import { useMutation, useQuery } from "@apollo/client";
import ActionDialog from "@dashboard/components/ActionDialog";
import useNotifier from "@dashboard/hooks/useNotifier";
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@material-ui/core";
import { Box, Multiselect, Option } from "@saleor/macaw-ui-next";
import React, { useState } from "react";

import {
  CMS_DELIVERY_VACATION_BULK_CREATE_MUTATION,
  CMS_DELIVERY_VACATION_CREATE_MUTATION,
} from "../cmsMutations";
import { CMS_DELIVERY_SLOTS_QUERY, CMS_DELIVERY_VACATIONS_QUERY } from "../cmsQueries";

interface VacationDialogProps {
  open: boolean;
  onClose: () => void;
}

export const VacationDialog: React.FC<VacationDialogProps> = ({ open, onClose }) => {
  const notify = useNotifier();
  const [mode, setMode] = useState<"single" | "range">("single");

  const [singleDate, setSingleDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [label, setLabel] = useState("");
  const [selectedSlotIds, setSelectedSlotIds] = useState<string[]>([]);

  const { data: slotsData } = useQuery(CMS_DELIVERY_SLOTS_QUERY);
  const slots = slotsData?.cmsDeliverySlots || [];

  const slotChoices: Option[] = slots.map((s: any) => ({
    label: `${s.label} (${s.slotKey})`,
    value: s.id,
  }));

  const [createSingle, { loading: creatingSingle }] = useMutation(
    CMS_DELIVERY_VACATION_CREATE_MUTATION,
    {
      refetchQueries: [{ query: CMS_DELIVERY_VACATIONS_QUERY }],
      onCompleted: data => {
        if (data.cmsDeliveryVacationCreate?.errors?.length) {
          notify({ status: "error", text: data.cmsDeliveryVacationCreate.errors[0].message });
        } else {
          notify({ status: "success", text: "Delivery blackout date created" });
          onClose();
        }
      },
    }
  );

  const [createBulk, { loading: creatingBulk }] = useMutation(
    CMS_DELIVERY_VACATION_BULK_CREATE_MUTATION,
    {
      refetchQueries: [{ query: CMS_DELIVERY_VACATIONS_QUERY }],
      onCompleted: data => {
        if (data.cmsDeliveryVacationBulkCreate?.errors?.length) {
          notify({ status: "error", text: data.cmsDeliveryVacationBulkCreate.errors[0].message });
        } else {
          notify({ status: "success", text: "Delivery blackout date range created" });
          onClose();
        }
      },
    }
  );

  const handleSubmit = () => {
    if (mode === "single") {
      if (!singleDate) {
        notify({ status: "error", text: "Date is required" });
        return;
      }
      createSingle({
        variables: {
          input: {
            date: singleDate,
            label,
            isActive: true,
            deliverySlotIds: selectedSlotIds,
          },
        },
      });
    } else {
      if (!startDate || !endDate) {
        notify({ status: "error", text: "Start and end dates are required" });
        return;
      }
      createBulk({
        variables: {
          startDate,
          endDate,
          label,
          deliverySlotIds: selectedSlotIds,
        },
      });
    }
  };

  const selectedSlotOptions = slotChoices.filter(choice => selectedSlotIds.includes(choice.value));

  return (
    <ActionDialog
      open={open}
      size="md"
      title="Add Delivery Vacation / Blackout Date"
      confirmButtonState={creatingSingle || creatingBulk ? "loading" : "default"}
      onClose={onClose}
      onConfirm={handleSubmit}
    >
      <Box display="flex" flexDirection="column" gap={4} width="100%">
        <RadioGroup row value={mode} onChange={e => setMode(e.target.value as any)}>
          <FormControlLabel value="single" control={<Radio color="primary" />} label="Single Date" />
          <FormControlLabel value="range" control={<Radio color="primary" />} label="Date Range (Bulk)" />
        </RadioGroup>

        {mode === "single" ? (
          <TextField
            label="Blackout Date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={singleDate}
            onChange={e => setSingleDate(e.target.value)}
            required
          />
        ) : (
          <Box display="grid" gridTemplateColumns={2} gap={2}>
            <TextField
              label="Start Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              required
            />
            <TextField
              label="End Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              required
            />
          </Box>
        )}

        <TextField
          label="Label / Reason (Optional)"
          fullWidth
          value={label}
          onChange={e => setLabel(e.target.value)}
          placeholder="e.g. Diwali Holiday, Monsoon Flood, Maintenance"
        />

        <Box display="flex" flexDirection="column" gap={1}>
          <Multiselect
            size="medium"
            options={slotChoices}
            label="Blocked Delivery Slots (Empty = Block ALL Slots)"
            placeholder="Search and select specific slots or leave empty..."
            value={selectedSlotOptions}
            onChange={values => {
              setSelectedSlotIds(values.map((v: any) => (typeof v === "string" ? v : v.value)));
            }}
          />
        </Box>
      </Box>
    </ActionDialog>
  );
};

export default VacationDialog;
